import { useEffect, useRef, useState } from 'react';
import { t } from '../i18n/I18n';
import { Icon } from './Icon';

export type AskToEditStep = 'idle' | 'sending' | 'sent' | 'role' | 'failed';

/**
 * «Ask to edit»: through `request` when a route for it is wired (lane B); without one the guest is told that only the
 * owner can let them edit, never that a request went — the demo projects are real catalog projects invitations open.
 */
export async function askToEdit({ request }: { request?: () => Promise<unknown> }): Promise<AskToEditStep> {
  if (!request) return 'role';
  try { await request(); return 'sent'; } catch { return 'failed'; }
}

/** The guest's pill at the bottom centre (Figma 1047:36214 «View only banner»); `sent` is the request's own state where its owner keeps one. */
export function ViewOnlyPill({ request, sent }: { request?: () => Promise<unknown>; sent?: boolean }) {
  const [step, setStep] = useState<AskToEditStep>('idle');
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const ask = () => {
    setStep('sending');
    void askToEdit({ request }).then(next => { if (alive.current) setStep(next); });
  };
  const waiting = sent ?? step === 'sent';
  const note = step === 'role' ? t('#WebAskOwnerForRole') : step === 'failed' ? t('#WebEditRequestFailed') : null;
  return <div className="view-only-dock">
    {note && <p className="view-only-note" role="status">{note}</p>}
    <div className="view-only-pill" role="group" aria-label={t('#OtherAccountOpenVisit')}>
      <span className="view-only-label" data-tip={t('#CanViewDesc')} data-tip-side="above"><Icon name="eye" size={18} /><span>{t('#OtherAccountOpenVisit')}</span></span>
      <button type="button" className="view-only-ask" disabled={step === 'sending' || waiting} aria-live="polite" onClick={ask}>
        {waiting ? t('#WebEditRequestSent') : t('#WebAskToEdit')}</button>
    </div>
  </div>;
}
